import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PanchTatvaCard from '../components/PanchTatvaCard';
import PanchTatvaButton from '../components/PanchTatvaButton';
import products from '../data/products';
import heroLogo from '../assets/images/PT_Logo_Transparent.png'; // Add your logo file here
import { useStrings } from '../i18n';

export default function Home() {
  const sectionRefs = useRef([]);
  const location = useLocation();
  const strings = useStrings();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('show');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
  }, []);

  // Only scroll if there's a specific scrollTo state AND it's not the initial load
  useEffect(() => {
    if (location.state?.scrollTo) {
      const id = location.state.scrollTo;
      const element = document.getElementById(id);
      if (element) {
        // Add a small delay to ensure DOM is fully rendered
        const timer = setTimeout(() => {
          // Calculate offset based on navbar height
          // Navbar is 80px on mobile (pt-20), 96px on desktop (pt-24)
          const isMobile = window.innerWidth < 768;
          const offset = isMobile ? 80 : 96;
          const targetY = element.offsetTop - offset;
          window.scrollTo({
            top: targetY,
            behavior: 'smooth',
            duration: 600,
          });
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [location.state?.scrollTo]);

  return (
    <div className="w-full">

      {/* Threshold / Hero */}
      <section
        id="hero"
        ref={(el) => (sectionRefs.current[0] = el)}
        className="fade-section relative bg-primaryBg text-headingText min-h-[60vh] md:h-[80vh] flex flex-col justify-center items-center text-center px-4 md:px-8 overflow-hidden"
      >
        {/* Logo Above H1 */}
        <img
          src={heroLogo}
          alt={strings.home.hero.alt}
          className="h-32 md:h-64 lg:h-80 mb-4 md:mb-6 object-contain w-full max-w-xs md:max-w-none"
          fetchpriority="high"
          decoding="async"
        />

        {/* H1: Only Panch Tatva */}
        {/* <h1 className="text-5xl md:text-6xl font-heading mb-6 animate-heading">
          Panch Tatva
        </h1> */}

        <p className="text-lg md:text-xl lg:text-2xl max-w-2xl mb-6 md:mb-10 text-bodyText px-2">
          {strings.home.hero.tagline}
        </p>

        {/* <PanchTatvaButton>{strings.home.hero.cta}</PanchTatvaButton> */}

        <div className="absolute top-10 left-10 w-4 h-4 md:w-6 md:h-6 bg-secondary rounded-full animate-floatSlow opacity-60"></div>
        <div className="absolute bottom-10 md:bottom-20 right-10 md:right-20 w-6 h-6 md:w-8 md:h-8 bg-secondary rounded-full animate-floatSlow opacity-40"></div>
      </section>

      {/* Elements / Recognition */}
      <section
        id="collection"
        ref={(el) => (sectionRefs.current[1] = el)}
        className="fade-section max-w-7xl mx-auto px-4 md:px-6 pt-12 md:pt-24"
      >
        <h2 className="text-3xl md:text-4xl font-heading text-headingText text-center mb-4 md:mb-6">
          {strings.home.collection.title}
        </h2>

        <p className="text-sm md:text-base text-bodyText text-center max-w-2xl mx-auto mb-8 md:mb-16 px-2">
          {strings.home.collection.description}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
          {products.map((product, i) => (
            <PanchTatvaCard
              key={product.id}
              {...product}
              image={product.elementImage || product.image}
              link={`/element/${product.element}`}
              objectFit="cover"
              className={`shimmer-hover fade-section delay-${i * 100}`}
            />
          ))}
        </div>

        {/* Find Your Element CTA — Feature 11: Quiz (only if enabled) */}
        {process.env.REACT_APP_ENABLE_AI_QUIZ === 'true' && (
          <div className="text-center mt-10 md:mt-16 pb-12 md:pb-20">
            <h3 className="text-2xl md:text-3xl font-heading text-headingText mb-3">
              {strings.quiz.cta}
            </h3>
            <p className="text-sm md:text-base text-bodyText mb-6 max-w-xl mx-auto px-2">
              {strings.quiz.ctaSubtitle}
            </p>
            <Link to="/quiz">
              <PanchTatvaButton size="large" variant="outline">
                {strings.quiz.cta}
              </PanchTatvaButton>
            </Link>
          </div>
        )}
      </section>

      {/* About / Grounding */}
      <section
        id="about"
        ref={(el) => (sectionRefs.current[2] = el)}
        className="fade-section max-w-5xl mx-auto py-12 md:py-20 px-4 md:px-6 space-y-4 md:space-y-6"
      >
        <h2 className="text-3xl md:text-5xl font-heading text-headingText text-center mb-6 md:mb-8">
          {strings.home.about.title}
        </h2>

        {strings.home.about.paragraphs.map((p, i) => (
          <p key={i} className="text-sm md:text-lg text-bodyText text-center leading-relaxed">
            {p}
          </p>
        ))}
      </section>

    </div>
  );
}
